// Thin wrapper around useSubscription that resolves the user's effective
// plan into a typed Plan object + entitlement helpers. Components should
// prefer this hook over poking at useSubscription directly so the plan
// schema stays centralised in src/lib/plans.ts.
//
// Server-side enforcement is a separate slice — for now `can()` is a
// best-effort client hint used to render upgrade prompts and lock UI.
import { useCallback, useMemo } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { PLANS, type Plan, type PlanId, planAtLeast } from "@/lib/plans";

const PREMIUM_MODEL_PREFIXES = [
  "openai/gpt-5", // gpt-5 + gpt-5-mini fall under the budgeted tier
  "google/gemini-2.5-pro",
];

function isPremiumModelId(modelId: string): boolean {
  return PREMIUM_MODEL_PREFIXES.some((p) => modelId.startsWith(p));
}

export type EntitlementCheck =
  | { allowed: true }
  | { allowed: false; reason: string; requires: PlanId };

export const useEntitlements = () => {
  const { user, loading: authLoading } = useAuth();
  const { status, loading: subLoading, refresh, startCheckout } = useSubscription();
  const { isAdmin, loading: roleLoading } = useUserRole();

  // Resolve the active plan id. Anonymous users + anyone without an active
  // subscription land on `script_kiddie` (the free tier). The
  // check-subscription function may return legacy plan strings (`pro`,
  // `crew`, `ai_hub_pro`) for grandfathered subscribers — map those to the
  // closest new tier. Admins always resolve to `zero_day` so they bypass
  // every paywall while dogfooding.
  const planId: PlanId = useMemo(() => {
    if (isAdmin) return "zero_day";
    if (!status?.active) return "script_kiddie";
    const raw = status.plan?.toLowerCase() ?? "";
    if (
      raw === "operator" ||
      raw === "hacker" ||
      raw === "wizard" ||
      raw === "architect" ||
      raw === "zero_day"
    ) {
      return raw as PlanId;
    }
    // Legacy aliases.
    if (raw === "crew") return "zero_day";
    if (raw === "pro" || raw === "ai_hub_pro") return "operator";
    return "script_kiddie";
  }, [status, isAdmin]);

  const plan: Plan = PLANS[planId];

  /** True if the user's plan is at least `required`. */
  const hasPlan = useCallback(
    (required: PlanId) => planAtLeast(planId, required),
    [planId],
  );

  /**
   * Capability check for a specific action. Currently only models are gated
   * client-side; quota counters will be added once usage tracking ships.
   */
  const can = useCallback(
    (action: { kind: "model"; modelId: string }): EntitlementCheck => {
      if (action.kind === "model") {
        if (!isPremiumModelId(action.modelId)) return { allowed: true };
        if (plan.quota.premiumModels) return { allowed: true };
        return {
          allowed: false,
          reason:
            "Premium models unlock at Operator — upgrade to use GPT-5 and Gemini 2.5 Pro.",
          requires: "operator",
        };
      }
      return { allowed: true };
    },
    [plan.quota.premiumModels],
  );

  return {
    user,
    loading: authLoading || subLoading || roleLoading,
    isAdmin,
    planId,
    plan,
    isPaid: planId !== "script_kiddie",
    hasPlan,
    can,
    isPremiumModel: isPremiumModelId,
    refresh,
    startCheckout,
    cancelAtPeriodEnd: !!status?.cancel_at_period_end,
    currentPeriodEnd: status?.current_period_end ?? null,
    /** Raw Stripe subscription status — 'active' / 'trialing' / 'inactive' / etc. */
    stripeStatus: status?.stripeStatus ?? "inactive",
    /** Set when Pro came from a coupon or admin grant rather than Stripe. */
    grantSource: status?.grant_source ?? null,
  };
};
